<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'

/**
 * RouterTransition：路由切换的淡入淡出动画。
 *
 * 用法（包裹 RouterView）：
 *   <ErrorBoundary>
 *     <RouterTransition>
 *       <RouterView />
 *     </RouterTransition>
 *   </ErrorBoundary>
 *
 * 行为：
 * - 路由切换时短暂变透明再恢复
 * - 尊重 prefers-reduced-motion：禁用动画
 * - 初次出现受 html.app-ready 类控制（main.ts 添加）
 */
const router = useRouter()
const transitioning = ref(false)
const prefersReducedMotion = ref(false)

onMounted(() => {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    prefersReducedMotion.value = mql.matches
    mql.addEventListener('change', (e) => {
      prefersReducedMotion.value = e.matches
    })
  }
})

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (prefersReducedMotion.value) return
    transitioning.value = true
    requestAnimationFrame(() => {
      setTimeout(() => {
        transitioning.value = false
      }, 220)
    })
  },
)
</script>

<template>
  <div class="router-transition" :class="{ transitioning, noMotion: prefersReducedMotion }">
    <slot />
  </div>
</template>

<style scoped>
.router-transition {
  opacity: 1;
  transition: opacity 200ms ease-out;
}
.router-transition.transitioning {
  opacity: 0;
}
.router-transition.noMotion {
  transition: none !important;
  opacity: 1 !important;
}
</style>
